/**
 * Parser for the Babcock Ranch resident portal RSS feeds
 * (babcockranchliving.com, a CivicPlus site).
 *
 * These feeds are open — no login — and are the authoritative source for
 * town events and community alerts. They are what let the app show a real
 * "Food Truck Friday, Sept 4, 5-9 PM" instead of the media kit's vague
 * "nearly every Friday evening."
 *
 * Deliberately regex-based rather than DOM-based: this runs in the
 * Cloudflare Workers runtime, which has no DOMParser. HTMLRewriter exists
 * but is streaming-oriented and awkward for pulling structured fields out
 * of an escaped HTML blob inside an XML element.
 *
 * Feed shape (verified against saved fixtures in tests/fixtures/):
 *
 *   <item>
 *     <title>Food Truck Friday</title>
 *     <link>https://www.babcockranchliving.com/Calendar.aspx?EID=5521</link>
 *     <description>&lt;strong&gt;Event date:&lt;/strong&gt; September 4, 2026
 *       &lt;br&gt;&lt;strong&gt;Event Time: &lt;/strong&gt;05:00 PM - 09:00 PM
 *       &lt;br&gt;&lt;strong&gt;Location:&lt;/strong&gt; &lt;br&gt;Babcock Ranch, FL 33982
 *       [&lt;strong&gt;Description:&lt;/strong&gt;&lt;br&gt;...]</description>
 *     <calendarEvent:EventDates>September 4, 2026</calendarEvent:EventDates>
 *   </item>
 *
 * The Description block is OPTIONAL — Food Truck Friday has none.
 */

export type RanchFeedKind = 'community_events' | 'parks_events' | 'alerts';

/** Town RSS kinds plus the seeded Babcock National HOA snapshot. */
export type CommunityEventKind = 'community_events' | 'parks_events' | 'hoa_national';

export interface RanchEvent {
  /** Stable id from the portal's EID query param, else a title+date hash. */
  id: string;
  title: string;
  /** ISO calendar date, YYYY-MM-DD, in local town time. */
  date: string;
  /** 24h "HH:MM" local, or null when the feed omits a time. */
  startTime: string | null;
  endTime: string | null;
  location: string | null;
  description: string | null;
  url: string | null;
  kind: CommunityEventKind;
}

export interface RanchAlert {
  id: string;
  title: string;
  body: string;
  url: string | null;
  publishedAt: string | null;
}

const MONTHS: Record<string, number> = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
  july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
};

/** Decode the XML/HTML entities CivicPlus double-escapes into description. */
function decodeEntities(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&'); // must come last
}

function stripTags(s: string): string {
  return s
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function tag(block: string, name: string): string | null {
  // Escape the ':' in namespaced tags like calendarEvent:EventDates.
  const re = new RegExp(`<${name}>([\\s\\S]*?)</${name}>`, 'i');
  const m = block.match(re);
  return m ? m[1].trim() : null;
}

/** "September 4, 2026" -> "2026-09-04". Explicit month map, never Date parsing. */
export function parseFeedDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const m = raw.trim().match(/([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})/);
  if (!m) return null;
  const month = MONTHS[m[1].toLowerCase()];
  if (!month) return null;
  const day = Number(m[2]);
  const year = Number(m[3]);
  if (day < 1 || day > 31) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** "05:00 PM" -> "17:00". Returns null on anything unexpected. */
export function parseFeedTime(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const m = raw.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return null;
  let hour = Number(m[1]);
  const minute = Number(m[2]);
  const isPm = m[3].toUpperCase() === 'PM';
  if (hour === 12) hour = isPm ? 12 : 0;
  else if (isPm) hour += 12;
  if (hour > 23 || minute > 59) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

/**
 * Pull the portal's own record id out of a feed link, for a stable event
 * or alert id across refetches. Calendar links use EID, Alert Center links
 * use AID — matching only EID silently degraded every alert to a
 * title-derived id, which would churn if the title were ever edited.
 */
function recordIdFrom(url: string | null): string | null {
  if (!url) return null;
  const m = url.match(/[?&](?:EID|AID)=(\d+)/i);
  return m ? m[1] : null;
}

/**
 * Pull the value that follows a bold field label, up to the next label or
 * the end of the blob. Tolerates the inconsistent whitespace CivicPlus
 * emits inside the <strong> tags ("Event Time: " has a trailing space).
 */
function labelled(decoded: string, label: string): string | null {
  const re = new RegExp(
    `<strong>\\s*${label}\\s*:?\\s*</strong>([\\s\\S]*?)(?=<strong>|$)`,
    'i',
  );
  const m = decoded.match(re);
  if (!m) return null;
  const v = stripTags(m[1]);
  return v.length ? v : null;
}

export function parseRanchEvents(
  xml: string,
  kind: Extract<CommunityEventKind, 'community_events' | 'parks_events'>,
): RanchEvent[] {
  const blocks = xml.match(/<item>[\s\S]*?<\/item>/gi) ?? [];
  const events: RanchEvent[] = [];

  for (const block of blocks) {
    const title = stripTags(decodeEntities(tag(block, 'title') ?? ''));
    if (!title) continue;

    const url = tag(block, 'link');
    const decoded = decodeEntities(tag(block, 'description') ?? '');

    // Prefer the dedicated EventDates element; fall back to the label.
    const date =
      parseFeedDate(tag(block, 'calendarEvent:EventDates')) ??
      parseFeedDate(labelled(decoded, 'Event date'));
    if (!date) continue; // an event with no resolvable date is not useful

    let startTime: string | null = null;
    let endTime: string | null = null;
    const timeRaw = labelled(decoded, 'Event Time');
    if (timeRaw) {
      const parts = timeRaw.split(/\s*-\s*/);
      startTime = parseFeedTime(parts[0]);
      endTime = parts.length > 1 ? parseFeedTime(parts[1]) : null;
    }

    events.push({
      id: recordIdFrom(url) ?? `${kind}:${date}:${title}`,
      title,
      date,
      startTime,
      endTime,
      location: labelled(decoded, 'Location'),
      description: labelled(decoded, 'Description'),
      url,
      kind,
    });
  }

  events.sort(compareEvents);

  return events;
}

/** Chronological, then by start time, with untimed events last in a day. */
export function compareEvents(
  a: Pick<RanchEvent, 'date' | 'startTime' | 'title'>,
  b: Pick<RanchEvent, 'date' | 'startTime' | 'title'>,
): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  const at = a.startTime ?? '99:99';
  const bt = b.startTime ?? '99:99';
  if (at !== bt) return at < bt ? -1 : 1;
  return a.title.localeCompare(b.title);
}

/** Merge town RSS + HOA (or any other) lists; first source wins on id clash. */
export function mergeWhatsOnEvents(...lists: RanchEvent[][]): RanchEvent[] {
  const seen = new Set<string>();
  const out: RanchEvent[] = [];
  for (const list of lists) {
    for (const e of list) {
      if (seen.has(e.id)) continue;
      seen.add(e.id);
      out.push(e);
    }
  }
  out.sort(compareEvents);
  return out;
}

/** Inclusive window used by GET /community/events (`today` … `today + days`). */
export function filterCommunityEvents<T extends { date: string }>(
  events: T[],
  today: string,
  days: number,
  includePast = false,
): T[] {
  const horizonIso = addDaysIso(today, days);
  return events.filter((e) => (includePast || e.date >= today) && e.date <= horizonIso);
}

export function eventSourceLabel(kind: CommunityEventKind): string {
  if (kind === 'hoa_national') return 'National';
  if (kind === 'parks_events') return 'Parks & Rec';
  return 'Community';
}

export function parseRanchAlerts(xml: string): RanchAlert[] {
  const blocks = xml.match(/<item>[\s\S]*?<\/item>/gi) ?? [];
  const alerts: RanchAlert[] = [];
  for (const block of blocks) {
    const title = stripTags(decodeEntities(tag(block, 'title') ?? ''));
    if (!title) continue;
    const url = tag(block, 'link');
    alerts.push({
      id: recordIdFrom(url) ?? `alert:${title}`,
      title,
      body: stripTags(decodeEntities(tag(block, 'description') ?? '')),
      url,
      publishedAt: tag(block, 'pubDate'),
    });
  }
  return alerts;
}

/** Group events by ISO date, preserving chronological order. */
export function groupEventsByDate(events: RanchEvent[]): { date: string; events: RanchEvent[] }[] {
  const out: { date: string; events: RanchEvent[] }[] = [];
  for (const e of events) {
    const last = out[out.length - 1];
    if (last && last.date === e.date) last.events.push(e);
    else out.push({ date: e.date, events: [e] });
  }
  return out;
}

/**
 * Official portal destinations. These are CivicPlus / CivicRec pages the
 * town already publishes — not a booking flow we own. Deep-link only.
 */
export const RANCH_CALENDAR_URL = 'https://www.babcockranchliving.com/Calendar.aspx';
/** CivicRec catalog (Activities / Facility Rentals). Prefer -fl over the older -fl-2 host. */
export const REC1_CATALOG_URL = 'https://secure.rec1.com/FL/babcock-ranch-fl/catalog';
export const CIVIC_REC_ACCOUNT_URL = 'https://www.babcockranchliving.com/363/Civic-Rec-Account';
export const EVENTS_PROGRAMMING_URL = 'https://www.babcockranchliving.com/444/Events-Programming';
export const AMENITY_RENTALS_URL = 'https://www.babcockranchliving.com/362/How-To-Rent-Amenities';
export const PARKS_REC_EMAIL = 'parksandrec@babcockranch.com';
export const PARKS_REC_PHONE = '9416767199';
export const PARKS_REC_PHONE_DISPLAY = '(941) 676-7199';

/** Extra days after today that count as "this week" (today + 6 = 7 days). */
export const THIS_WEEK_EXTRA_DAYS = 6;

/** Extra days after today that count as "next 5 days" (today + 4 = 5 days). */
export const NEXT_5_DAYS_EXTRA = 4;

/** Add calendar days to an ISO date without using the device timezone. */
export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

export function isThisWeek(iso: string, today: string): boolean {
  if (!iso || !today) return false;
  return iso >= today && iso <= addDaysIso(today, THIS_WEEK_EXTRA_DAYS);
}

export function isNextFiveDays(iso: string, today: string): boolean {
  if (!iso || !today) return false;
  return iso >= today && iso <= addDaysIso(today, NEXT_5_DAYS_EXTRA);
}

export function eventsInNextFiveDays<T extends { date: string }>(events: T[], today: string): T[] {
  return events.filter((e) => isNextFiveDays(e.date, today));
}

/** First venue token, clipped so a hero row stays one line. */
export function shortEventLocation(location: string | null | undefined, max = 36): string | null {
  if (!location) return null;
  const first = location.split(',')[0].replace(/\s+/g, ' ').trim();
  if (!first) return null;
  if (first.length <= max) return first;
  return `${first.slice(0, max - 1).trimEnd()}…`;
}

/** "17:00" -> "5:00 PM". Feed times are already town-local. */
export function formatFeedTime12h(t: string | null): string | null {
  if (!t) return null;
  const [hStr, mStr] = t.split(':');
  const h24 = Number(hStr);
  if (!Number.isFinite(h24) || !mStr) return null;
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mStr} ${period}`;
}

export function formatEventTimeRange(e: Pick<RanchEvent, 'startTime' | 'endTime'>): string {
  const start = formatFeedTime12h(e.startTime);
  if (!start) return 'Time TBA';
  const end = formatFeedTime12h(e.endTime);
  return end ? `${start} – ${end}` : start;
}

/** Compact day label for Home / list rows, compared against town "today". */
export function formatEventDayLabel(iso: string, today: string): string {
  if (iso === today) return 'Today';
  if (iso === addDaysIso(today, 1)) return 'Tomorrow';
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' });
}
